import React, { useState, useEffect } from 'react';
import { ExternalLink, Link as LinkIcon } from 'lucide-react';

/**
 * Link/CTA Destination Editor
 * Allows editing the href/destination of CTA elements
 */
export default function LinkEditor({ element, onUpdate }) {
  const [url, setUrl] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (element?.href) {
      setUrl(element.href);
    }
  }, [element]);

  const handleChange = (e) => {
    setUrl(e.target.value);
  };

  const handleApply = () => {
    if (!element?.dataPath || isUpdating) return;
    
    setIsUpdating(true);
    
    try {
      // For CTAs, the link is typically stored in a .href property within the data object
      // e.g., if dataPath is "copy.heroPrimaryCta", the href is at "copy.heroPrimaryCta.href"
      const linkPath = element.dataPath.includes('.href') 
        ? element.dataPath 
        : `${element.dataPath}.href`;
      
      onUpdate(linkPath, url);
      console.log('Link updated:', linkPath, '→', url);
    } catch (error) {
      console.error('Error updating link:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-700 flex items-center gap-1">
        <LinkIcon className="w-3 h-3" />
        Link Destination
      </label>
      <input
        type="text"
        value={url}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        placeholder="https://example.com or #"
        disabled={isUpdating}
      />
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <ExternalLink className="w-3 h-3" />
          Enter full URL or anchor
        </p>
        <button
          onClick={handleApply}
          disabled={isUpdating}
          className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isUpdating ? 'Applying...' : 'Apply'}
        </button>
      </div>
      {element?.dataPath && (
        <p className="text-xs text-gray-400 font-mono">
          {element.dataPath}{element.dataPath.includes('.href') ? '' : '.href'}
        </p>
      )}
    </div>
  );
}

